<template>
  <div class="category-selector">
    <div class="flex gap-2">
      <div class="flex-1">
        <div class="custom-dropdown" :class="{ 'is-open': isDropdownOpen }">
          <div 
            class="dropdown-trigger"
            @click.stop="toggleDropdown"
          >
            <span v-if="selectedCategory" class="selected-category">
              <span 
                class="category-color-dot"
                :style="{ backgroundColor: selectedCategory.color }"
              ></span>
              {{ selectedCategory.name }}
            </span>
            <span v-else class="placeholder">
              {{ placeholder || $t('BUSINESS_DATA.CATEGORY.SELECT_CATEGORY') }}
            </span>
            <fluent-icon 
              icon="chevron-down" 
              size="16" 
              class="dropdown-arrow"
              :class="{ 'is-rotated': isDropdownOpen }"
            />
          </div>
          
          <div v-if="isDropdownOpen" class="dropdown-menu" @click.stop>
            <div
              v-for="category in categories"
              :key="category.id"
              class="dropdown-item"
              @click.stop="selectCategory(category)"
            >
              <span 
                class="category-color-dot"
                :style="{ backgroundColor: category.color }"
              ></span>
              {{ category.name }}
            </div>
          </div>
        </div>
      </div>
      
      <woot-button
        type="button"
        class="!text-grass-500"
        variant="clear"
        size="small"
        @click="showCreateModal = true"
      >
        <fluent-icon icon="add" size="16" />
        {{ $t('BUSINESS_DATA.CATEGORY.ADD_NEW') }}
      </woot-button>
    </div>
    
    <!-- Selected category preview -->
    <div v-if="selectedCategory" class="category-preview">
      <span
        class="category-badge"
        :style="{ 
          backgroundColor: selectedCategory.color, 
          color: getTextColor(selectedCategory.color) 
        }"
      >
        {{ selectedCategory.name }}
      </span>
    </div>
    
    <!-- Create category modal -->
    <CategoryModal
      :show="showCreateModal"
      :category-type="categoryType"
      @close="showCreateModal = false"
      @created="onCategoryCreated"
      @updated="onCategoryUpdated"
    />
  </div>
</template>

<script>
import CategoryModal from './CategoryModal.vue';

export default {
  name: 'CategorySelector',
  components: { CategoryModal },
  
  props: {
    // Vue 2 v-model
    value: {
      type: [String, Number],
      default: null,
    },
    // Vue 3 v-model
    modelValue: {
      type: [String, Number],
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    categoryType: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      showCreateModal: false,
      isDropdownOpen: false,
      localSelectedId: null,
    };
  },
  
  computed: {
    selectedCategoryId() {
      return this.localSelectedId;
    },
    
    selectedCategory() {
      if (!this.selectedCategoryId) return null;
      return this.categories.find(cat => cat.id == this.selectedCategoryId);
    },
  },
  
  watch: {
    categories: {
      immediate: true,
      handler() {
        if (!this.categories.find(cat => cat?.id === this.localSelectedId)) {
          this.localSelectedId = null;
        }
      },
    },
    // Support Vue 2 v-model (value) and Vue 3 v-model (modelValue)
    value: {
      immediate: true,
      handler(newVal) {
        this.localSelectedId = newVal;
      },
    },
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.localSelectedId = newVal;
      },
    },
  },

  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    
    selectCategory(category) {
      const newValue = category ? category.id : null;
      this.localSelectedId = newValue;
      // Emit for both Vue 2 and Vue 3 consumers
      this.$emit('input', newValue);
      this.$emit('update:modelValue', newValue);
      this.isDropdownOpen = false;
    },
    
    getTextColor(backgroundColor) {
      if (!backgroundColor) return '#000000';
      
      // Convert hex to RGB
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    onCategoryCreated(newCategory) {
      // Add new category to the list
      this.$emit('category-created', newCategory);
      this.$emit('update:modelValue', newCategory.id);
      // Select the new category
      this.localSelectedId = newCategory.id;
      this.showCreateModal = false;
    },

    onCategoryUpdated() {
      this.$emit('category-updated');
      this.showCreateModal = false;
    },
  },
  
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.isDropdownOpen = false;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.category-selector {
  .custom-dropdown {
    position: relative;
    width: 100%;

    .dropdown-trigger {
      @apply flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 transition-colors duration-200 cursor-pointer dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100;

      .selected-category,
      .placeholder {
        @apply flex items-center gap-2 text-sm;
      }

      .placeholder {
        @apply text-slate-500 dark:text-slate-400;
      }

      .category-color-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .dropdown-arrow {
        @apply text-slate-400 dark:text-slate-500 transition-transform duration-200;

        &.is-rotated {
          transform: rotate(180deg);
        }
      }


    }

    .dropdown-menu {
      @apply absolute z-[1000] left-0 right-0 mt-2 rounded-lg border border-slate-200 bg-white shadow-xl max-h-52 overflow-y-auto dark:border-slate-700 dark:bg-slate-900;

      .dropdown-item {
        @apply flex items-center gap-2 px-3 py-2 text-sm text-slate-700 cursor-pointer transition-colors duration-200 dark:text-slate-200;

        &:hover {
          @apply bg-slate-100 dark:bg-slate-800;
        }

        &:first-child {
          @apply rounded-t-lg;
        }

        &:last-child {
          @apply rounded-b-lg;
        }

        .category-color-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
        }
      }
    }

   
  }
}

.category-preview {
  @apply mt-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/70;
}

.category-badge {
  @apply inline-block px-3 py-1 rounded-full text-sm font-medium;
}
</style>
